import React, { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { type CreatePlatformInviteRequest, inviteCreatePlatformInvite } from '@column/api';
import { Button, Dropdown, DropdownOption, Icon, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { NotificationList } from '~/components';
import { precannedRolesByID, precannedRolesByName } from '~/repositories/RoleRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FormElement, FormLabel, Line, Paragraph } from '~/styles';
import { ApiError } from '~/typings/API';
import { validateEmail } from '~/util';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  roles: DropdownOption[];
}

interface InviteEntry {
  email: string;
  roleId: string;
}

interface FormValues {
  inviteEntries: InviteEntry[];
}

const Form = styled.form`
  display: grid;
  grid-gap: 24px;
  padding: 0 24px;
`;

const Fields = styled.div`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: auto-fill;
  text-align: left;
`;

const Inner = styled.div`
  display: grid;
  grid-gap: 16px;
`;

const Columns = styled.div`
  margin-top: -2px;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 40% auto;
`;

const AddAnother = styled(Button)`
  margin-top: 4px;
  padding: 0;
  justify-self: start;
`;

const Email = styled(Input)`
  display: flex;
  align-items: center;

  & > div {
    flex-grow: 1;
  }
`;

const Remove = styled(Icon.Cross)`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  cursor: pointer;
  margin-top: -2px;
  margin-bottom: -2px;

  path {
    transition: stroke 0.2s;
  }

  &:hover {
    --icon-color: ${({ theme }) => theme.secondary.background};
  }
`;

const RoleHelpButton = styled(Button)`
  padding: 0;
  font-weight: 500;
  --button-color: ${({ theme }) => theme.foreground};

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  }

  &:hover {
    --button-color: ${({ theme }) => theme.primary.background};

    svg {
      --icon-color: ${({ theme }) => theme.primary.background};
    }
  }
`;

const AdminRoleId = precannedRolesByName.admin.id;

export const InviteUsersModal: React.FC<Props> = ({ onClose, onSave, open, roles }) => {
  const { currentPlatform } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      inviteEntries: [{ roleId: AdminRoleId, email: '' }],
    },
    mode: 'onBlur',
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'inviteEntries',
  });

  useEffect(() => {
    if (open) {
      reset({
        inviteEntries: [{ roleId: AdminRoleId, email: '' }],
      });
    }
  }, [open, reset]);

  const onSubmit = ({ inviteEntries }: FormValues) => {
    const platformId = currentPlatform?.id;

    if (!platformId) {
      addDangerNotification({
        content: 'No Platform ID provided, please reload and try again',
        display: 'popup',
      });
      return;
    }

    const validEmails = inviteEntries.filter((entry: InviteEntry) => entry.email.length > 0);

    if (validEmails.length < 1) {
      addDangerNotification({
        content: 'Enter a valid email address',
        display: 'popup',
      });
      return;
    }

    Promise.all(
      validEmails.map((entry: InviteEntry) => {
        const platformRole =
          precannedRolesByID[entry.roleId] !== undefined ? precannedRolesByID[entry.roleId].name : undefined;
        const roleId = platformRole === undefined ? entry.roleId : undefined;

        const body: CreatePlatformInviteRequest = {
          platform_id: platformId,
          email: entry.email,
          platform_role: platformRole,
          role_id: roleId,
        };

        return inviteCreatePlatformInvite({ path: { platform_id: platformId }, body, throwOnError: true });
      })
    )
      .then((results) => {
        onSave();
        const noun = validEmails.length === 1 ? 'Invite' : 'Invites';
        const emails = validEmails.map((emailEntry) => emailEntry.email).join(', ');
        addSuccessNotification({
          content: results.some((result) => result.response.status === 202)
            ? `${noun} for "${emails}" submitted for approval.`
            : `${noun} sent to "${emails}".`,
        });
      })
      .catch((error: ApiError) => {
        const isLimitExceeded = error?.code === 'limit_exceeded';
        const limitMessage = (error?.details as { limit?: string })?.limit;
        addDangerNotification({
          content: isLimitExceeded && limitMessage ? limitMessage : error.message || 'An error occurred',
          display: 'popup',
        });
      });
  };

  const handleAddEmail = () => {
    append({ roleId: AdminRoleId, email: '' });
  };

  const removeEmail = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    remove(index);
  };

  return (
    <Modal onClose={onClose} size={ModalSize.Medium} isOpen={open}>
      <Modal.IconCircle type="primary" icon="AnimationMailbox" />

      <Modal.Content>
        <Modal.Headline fullWidth>Invite users</Modal.Headline>
        <Paragraph fullWidth>Select a role for each email.</Paragraph>
      </Modal.Content>

      <NotificationList display="popup" />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fields>
          <Inner>
            {fields.map((field, index: number) => (
              <Inner key={field.id}>
                {index !== 0 && <Line />}
                <Columns>
                  <FormElement>
                    <FormLabel>
                      {index === 0 ? (
                        <RoleHelpButton
                          icon={<Icon.CircleQuestionmark />}
                          iconRight
                          onClick={() => openHelpSidebar('Roles')}
                          variant="subtle"
                          size="small"
                          type="button"
                        >
                          Role
                        </RoleHelpButton>
                      ) : (
                        'Role'
                      )}
                    </FormLabel>
                    <Controller
                      control={control}
                      name={`inviteEntries.${index}.roleId`}
                      render={({ field: roleField }) => (
                        <Dropdown
                          placeholder="Select role"
                          onChange={roleField.onChange}
                          active={roleField.value}
                          options={roles}
                          fullWidth
                        />
                      )}
                    />
                  </FormElement>
                  <FormElement>
                    <FormLabel>Email</FormLabel>
                    <Controller
                      control={control}
                      name={`inviteEntries.${index}.email`}
                      rules={{
                        validate: (email) => !email || validateEmail(email) || 'Enter a valid email address',
                      }}
                      render={({ field: emailField, fieldState: { error } }) => (
                        <Email
                          value={emailField.value}
                          onChange={emailField.onChange}
                          onBlur={emailField.onBlur}
                          ref={emailField.ref}
                          placeholder="Email address"
                          hasError={!!error}
                        >
                          {index !== 0 && <Remove onClick={(e: React.MouseEvent) => removeEmail(e, index)} />}
                        </Email>
                      )}
                    />
                  </FormElement>
                </Columns>
              </Inner>
            ))}
            {fields.length < 5 && (
              <AddAnother size="small" type="button" icon={<Icon.Plus />} variant="subtle" onClick={handleAddEmail}>
                Add Another
              </AddAnother>
            )}
            <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
          </Inner>
        </Fields>
      </Form>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} type="button" size="small">
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)} type="submit" size="small">
          Send
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
